import { WhatsAppIcon } from '@/components/WhatsAppIcon';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontWhatsApp } from '@/features/storefront/hooks/useStorefrontWhatsApp';
import { useProductParts } from '@/features/storefront/parts/context';
import type { ProductPartProps } from '@/features/storefront/parts/ProductParts';
import { partIsWide, partOwnsSpacing } from '@/features/storefront/parts/style';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { partFlag, type PagePart } from '@/features/storefront/sections/parts';
import { cn } from '@/lib/cn';

/**
 * The product page's way into the merchant's WhatsApp chat.
 *
 * Under "Add to cart" on every product page by default, and drawing nothing
 * until the merchant has connected a number and switched it on - which is what
 * lets it sit in the default arrangement without changing a single shop that
 * has not. An outline button in the shop's own primary, so it reads as the
 * secondary action beside the solid one above it; the WhatsApp mark is what
 * says which app is about to open.
 */

/** The size the theme wanted for a margin, where the merchant has not chosen. */
function ownClass(part: PagePart, fallback: string): string | undefined {
  return partOwnsSpacing(part) ? fallback : undefined;
}

/** `Size M, Colour Indigo` - the chosen options, written out for the merchant. */
function variantLabel(options: Record<string, unknown> | undefined): string | undefined {
  const chosen = Object.values(options ?? {}).map(String).filter(Boolean);
  return chosen.length > 0 ? chosen.join(', ') : undefined;
}

/** The product's own address, absolute, for a chat on another device. */
function productUrl(path: string): string {
  if (typeof window === 'undefined') return path;
  return new URL(path, window.location.origin).href;
}

export function ProductBuyOnWhatsAppPart({ part, presentation }: ProductPartProps) {
  const text = useShopContent();
  const paths = useStorefrontPaths();
  const product = useProductParts();
  const { whatsapp, buyUrl } = useStorefrontWhatsApp();
  if (!product || !whatsapp?.enabled || !whatsapp.buy_on_whatsapp) return null;

  const href = buyUrl({
    name: product.detail.name,
    url: productUrl(paths.product(product.detail.slug)),
    variantLabel: variantLabel(product.selected?.options),
  });
  if (!href) return null;

  const wide = partIsWide(part, presentation);

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={cn(
        ownClass(part, 'mt-3'),
        'inline-flex h-12 items-center justify-center gap-2 border px-7 text-sm font-semibold',
        wide ? 'w-full' : 'w-full sm:w-auto',
      )}
      style={{
        backgroundColor: 'transparent',
        color: 'var(--storefront-primary)',
        borderColor: 'var(--storefront-primary)',
        borderRadius: 'var(--storefront-radius)',
      }}
    >
      {partFlag(part, 'show_icon') ? <WhatsAppIcon className="size-4" aria-hidden /> : null}
      {part.label.trim() || text('product_page.buy_on_whatsapp')}
    </a>
  );
}
